import {
    type AccountInfo,
    BrowserAuthError,
    InteractionRequiredAuthError,
    PublicClientApplication,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { type ReactNode, useEffect, useState } from "react";
import { scopesNeeded } from "src/utils/authConfig";

// Get auth config from environment - required, no fallbacks
const clientId = import.meta.env.VITE_AUTH_CLIENT_ID;
const tenantId = import.meta.env.VITE_AUTH_TENANT_ID;

if (!clientId || !tenantId) {
    throw new Error(
        "Widget authentication configuration missing. Please ensure VITE_AUTH_CLIENT_ID and VITE_AUTH_TENANT_ID environment variables are set.",
    );
}

// Widget Auth Config - clientId and tenantId are set from environment variables

// Widget-specific MSAL configuration for iframe usage
const msalConfig = {
    auth: {
        clientId: clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin, // Use origin like main app
        postLogoutRedirectUri: window.location.origin,
        navigateToLoginRequestUrl: false, // Don't navigate in popup mode
    },
    cache: {
        cacheLocation: "localStorage" as const,
        storeAuthStateInCookie: true, // Enable for IE11 or Edge support
    },
    system: {
        allowRedirectInIframe: false, // Keep false for security
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
        loadFrameTimeout: 0,
        asyncPopups: false,
    },
};

// Initialize MSAL instance for widget
const msalInstance = new PublicClientApplication(msalConfig);

interface WidgetAuthProviderProps {
    children: ReactNode;
    authMode?: "sso" | "popup" | "inherit";
    loginHint?: string;
    sid?: string;
}

export function WidgetAuthProvider({
    children,
    authMode = "popup",
    loginHint,
    sid,
}: WidgetAuthProviderProps) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [account, setAccount] = useState<AccountInfo | null>(null);

    useEffect(() => {
        const initAuth = async () => {
            try {
                // Check if we already have an account
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    setAccount(accounts[0]);
                    setIsAuthenticated(true);
                    setIsLoading(false);
                    return;
                }

                // Try SSO silent authentication
                if (authMode === "sso" || authMode === "inherit") {
                    try {
                        const ssoRequest = {
                            scopes: scopesNeeded,
                            ...(loginHint && { loginHint }),
                            ...(sid && { sid }),
                        };

                        const response = await msalInstance.ssoSilent(ssoRequest);
                        setAccount(response.account);
                        setIsAuthenticated(true);
                    } catch (ssoError) {
                        console.error("SSO Silent failed:", ssoError);

                        if (ssoError instanceof InteractionRequiredAuthError) {
                            // User interaction required, show login button
                            setError("Authentication required. Please sign in.");
                        } else {
                            setError("SSO authentication failed.");
                        }
                    }
                }
            } catch (error) {
                console.error("Auth initialization error:", error);
                setError("Failed to initialize authentication.");
            } finally {
                setIsLoading(false);
            }
        };

        initAuth();

        // Listen for authentication messages from parent
        const handleMessage = async (event: MessageEvent) => {
            if (event.data?.type === "cts-auth") {
                const { loginHint, sid, accessToken } = event.data;

                if (accessToken) {
                    // If parent provides access token, we could validate it
                    // For now, we'll attempt SSO with hints
                    try {
                        const response = await msalInstance.ssoSilent({
                            scopes: scopesNeeded,
                            loginHint,
                            sid,
                        });
                        setAccount(response.account);
                        setIsAuthenticated(true);
                        setError(null);
                    } catch (error) {
                        console.error("Parent auth sync failed:", error);
                    }
                }
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [authMode, loginHint, sid]);

    const handleLogin = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const loginRequest = {
                scopes: scopesNeeded,
                ...(loginHint && { loginHint }),
                prompt: "select_account", // Force account selection
            };

            // Use popup for iframe authentication
            const response = await msalInstance.loginPopup(loginRequest);
            setAccount(response.account);
            setIsAuthenticated(true);

            // Notify parent of successful login
            window.parent.postMessage(
                {
                    type: "cts-auth-success",
                    account: {
                        username: response.account.username,
                        name: response.account.name,
                    },
                },
                "*",
            );
        } catch (error) {
            console.error("Login error:", error);

            if (error instanceof BrowserAuthError) {
                if (error.errorCode === "popup_window_error") {
                    setError("Popups are blocked. Please allow popups for this site.");
                } else {
                    setError(`Login failed: ${error.message}`);
                }
            } else {
                setError("Login failed. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            // Use popup logout for iframe
            await msalInstance.logoutPopup({
                account: account || undefined,
            });

            setIsAuthenticated(false);
            setAccount(null);

            // Notify parent of logout
            window.parent.postMessage(
                {
                    type: "cts-auth-logout",
                },
                "*",
            );
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                }}
            >
                <div>Initializing...</div>
            </div>
        );
    }

    // Not authenticated - show login UI
    if (!isAuthenticated) {
        return (
            <MsalProvider instance={msalInstance}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100vh",
                        backgroundColor: "#f5f5f5",
                    }}
                >
                    <div
                        style={{
                            maxWidth: "400px",
                            width: "100%",
                            padding: "32px",
                            backgroundColor: "white",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            border: "1px solid #e5e7eb",
                        }}
                    >
                        <div style={{ textAlign: "center" }}>
                            <h2
                                style={{
                                    fontSize: "20px",
                                    fontWeight: "500",
                                    marginBottom: "8px",
                                    color: "#1a1a1a",
                                }}
                            >
                                CTS Knowledge Search
                            </h2>
                            <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "24px" }}>
                                Sign in to access technical documentation
                            </p>

                            {error && (
                                <p
                                    style={{
                                        color: "#dc2626",
                                        fontSize: "14px",
                                        marginBottom: "16px",
                                    }}
                                >
                                    {error}
                                </p>
                            )}

                            <button
                                type="button"
                                onClick={handleLogin}
                                disabled={isLoading}
                                style={{
                                    width: "100%",
                                    padding: "12px 24px",
                                    backgroundColor: isLoading ? "#9ca3af" : "#0066cc",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    fontSize: "16px",
                                    fontWeight: "500",
                                    cursor: isLoading ? "not-allowed" : "pointer",
                                    transition: "background-color 0.2s",
                                    marginBottom: "16px",
                                }}
                                onMouseEnter={(e) => {
                                    if (!isLoading)
                                        e.currentTarget.style.backgroundColor = "#0052a3";
                                }}
                                onMouseLeave={(e) => {
                                    if (!isLoading)
                                        e.currentTarget.style.backgroundColor = "#0066cc";
                                }}
                            >
                                {isLoading ? "Loading..." : "Sign in with Microsoft"}
                            </button>

                            <p style={{ fontSize: "12px", color: "#9ca3af", margin: 0 }}>
                                This will open a popup window for authentication
                            </p>
                        </div>
                    </div>
                </div>
            </MsalProvider>
        );
    }

    // Authenticated - render children
    return (
        <MsalProvider instance={msalInstance}>
            <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
                {/* Optional: Show user info header */}
                {account && (
                    <div
                        style={{
                            padding: "8px 16px",
                            borderBottom: "1px solid #e0e0e0",
                            backgroundColor: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            Signed in as: {account.username}
                        </div>
                        <button
                            type="button"
                            onClick={handleLogout}
                            style={{
                                padding: "4px 12px",
                                backgroundColor: "transparent",
                                color: "#0066cc",
                                border: "none",
                                borderRadius: "4px",
                                fontSize: "12px",
                                cursor: "pointer",
                                transition: "background-color 0.2s",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#f3f4f6";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                            }}
                        >
                            Sign out
                        </button>
                    </div>
                )}

                <div style={{ flex: 1, overflow: "hidden" }}>{children}</div>
            </div>
        </MsalProvider>
    );
}
